import { Box, Chip, Typography } from '@mui/material';

import {
  composeCredentialLine,
  resolveOverrideLines,
  CREDENTIALS_BLANK_NOTE,
  SIGNATURE_BLANK_NOTE,
} from './signature-override';

type SignatureRenderProps = {
  name: string;
  license?: string | null;
  credentials?: string | null;
  //  Show the "FROM HR & LICENSE RECORDS" provenance badge (setup / profile view).
  showBadge?: boolean;
  //  APIT-655: the therapist's display overrides for the two lines of the block.
  //  Absent for every therapist today, in which case nothing below changes.
  signatureLineOverride?: string | null;
  credentialsLineOverride?: string | null;
};

/**
 * APIT-655 — the note shown where a line has been deliberately left blank.
 *
 * ## This must not look like a signature
 *
 * It is the one piece of text in this component that will *not* appear on the PDF,
 * sitting where a therapist expects to see what will. Rendered in the same style as
 * real signature content, a therapist would reasonably read "Signature line
 * intentionally blank" as a sentence that prints on the document — and would be
 * signing something they had misread.
 *
 * So it is made unmistakably an annotation, on every axis the signature uses:
 *
 * - **font** — the theme's UI sans, never the script face the signature is drawn in;
 * - **size** — caption, against the signature's 32px;
 * - **weight and slant** — italic, which nothing else in the block is;
 * - **colour** — `text.disabled`, the dimmest role in the palette;
 * - **enclosure** — a dashed outline on a tinted ground. Nothing that prints has a
 *   border, so the box alone reads as chrome even before the text is read.
 *
 * The dashed border does most of the work: it is a convention for "placeholder,
 * not content", and it survives the theme being dark, the dialog being narrow, and
 * the script font failing to load — which it does today, since 'Dancing Script' is
 * neither bundled nor linked and falls through to a system cursive.
 */
function BlankLineNote({ label }: { label: string }) {
  return (
    <Box
      data-testid="signature-blank-note"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        border: '1px dashed',
        borderColor: 'divider',
        borderRadius: 0.75,
        bgcolor: 'action.hover',
        px: 1,
        py: 0.25,
      }}
    >
      <Typography
        variant="caption"
        sx={{ fontStyle: 'italic', color: 'text.disabled', lineHeight: 1.6 }}
      >
        {label}
      </Typography>
    </Box>
  );
}

//  THIN-7681: view-only signature rendered from the HR/licensure name in a script font,
//  with the license number. Never free-typed — the signed name always matches the license.
//
//  APIT-655: unless the therapist's record overrides one of the two lines. The preview
//  has to reflect what will actually be printed, or they preview one thing and sign
//  another — so the override rules are applied here exactly as reform-sign applies
//  them, including splitting a value into several lines on `,` and newline.
export default function SignatureRender({
  name,
  license,
  credentials,
  showBadge,
  signatureLineOverride,
  credentialsLineOverride,
}: SignatureRenderProps) {
  //  The default line list for each half. Passing the default through
  //  `resolveOverrideLines` rather than branching around it is what keeps the
  //  no-override path identical to the one that was here before: one signature
  //  line holding `name`, and one credentials line holding the composed text,
  //  rendered only when there is something to render.
  const signature = resolveOverrideLines(signatureLineOverride, [name]);

  const defaultCredentials = composeCredentialLine(credentials, license);
  const credentialLines = resolveOverrideLines(
    credentialsLineOverride,
    defaultCredentials ? [defaultCredentials] : []
  );

  return (
    <Box
      sx={{
        position: 'relative',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        px: 2,
        py: 1.5,
        minHeight: 68,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
        bgcolor: 'background.paper',
      }}
    >
      {/*  `minWidth: 0` lets this half shrink inside the flex row. Without it a long
           line pushes the credentials off the edge instead of wrapping. */}
      <Box sx={{ minWidth: 0 }}>
        {signature.suppressed ? (
          <BlankLineNote label={SIGNATURE_BLANK_NOTE} />
        ) : (
          signature.lines.map((line, index) => (
            <Typography
              key={`${index}-${line}`}
              sx={{
                fontFamily:
                  "'Dancing Script', 'Segoe Script', 'Brush Script MT', cursive",
                fontSize: 32,
                lineHeight: 1.1,
                color: 'text.primary',
                //  APIT-655: this used to be `nowrap` + `ellipsis`, which silently
                //  truncated. A preview that shows "Jonathan Wetherspo…" beside a
                //  document that prints the whole name is the exact failure this
                //  component exists to prevent, and an override is more likely to be
                //  long than a name is. Wrap instead of hiding, and break a single
                //  over-long word rather than letting it overflow the box.
                overflowWrap: 'anywhere',
              }}
            >
              {line}
            </Typography>
          ))
        )}
      </Box>
      <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
        {showBadge && (
          <Chip
            size="small"
            label="FROM HR & LICENSE RECORDS"
            sx={{ mb: 0.5, fontSize: 10, fontWeight: 600, letterSpacing: 0.3 }}
          />
        )}
        {credentialLines.suppressed ? (
          <BlankLineNote label={CREDENTIALS_BLANK_NOTE} />
        ) : (
          credentialLines.lines.map((line, index) => (
            <Typography
              key={`${index}-${line}`}
              variant="caption"
              color="text.secondary"
              display="block"
            >
              {line}
            </Typography>
          ))
        )}
      </Box>
    </Box>
  );
}
